import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Award, 
  Users, 
  Shield, 
  Heart,
  ArrowRight,
  CheckCircle,
  Phone,
  Mail
} from 'lucide-react';

const values = [
  {
    icon: Heart,
    title: 'Client First',
    description: 'Your goals are our priority. We take the time to understand your unique situation and find solutions that work for you.'
  },
  {
    icon: Shield,
    title: 'Trust & Transparency',
    description: "No hidden fees, no pushy sales tactics. We're upfront about everything and always act in your best interest."
  },
  {
    icon: Award,
    title: 'Expert Knowledge',
    description: 'Our team stays up-to-date with the latest market changes, lender policies, and government schemes to give you the best advice.'
  },
  {
    icon: Users,
    title: 'Long-term Relationships',
    description: "We're not just here for one loan. We build lasting relationships and continue to support you throughout your property journey."
  }
];

const team = [
  {
    name: 'Jyotsna Arora',
    role: 'Mortgage Broker',
    bio: 'Dedicated to helping clients achieve their property dreams with personalised service.',
    phone: '0468 883 356',
    email: 'Jyotsna@loansandbeyond.com',
    image: 'https://customer-assets.emergentagent.com/job_91a71442-3d91-42b3-99c8-8a1badec3ba9/artifacts/r29ina3b_Jo%20Pic.jpg'
  },
  {
    name: 'Nidhi Saini',
    role: 'Mortgage Broker',
    bio: 'Expert guidance through the home loan process with a focus on first home buyers.',
    phone: '0452 510 447',
    email: 'Nidhi@loansandbeyond.com',
    image: 'https://customer-assets.emergentagent.com/job_91a71442-3d91-42b3-99c8-8a1badec3ba9/artifacts/hc7253oy_WhatsApp%20Image%202026-02-03%20at%209.18.23%20AM.jpeg'
  }
];

export default function AboutPage() {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 gradient-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-[#374151]/10 text-[#374151] rounded-full text-sm font-medium mb-6">
                About Us
              </span>
              <h1 className="font-heading text-5xl md:text-6xl font-bold text-[#374151] leading-tight mb-6">
                Your Trusted Australian Mortgage Partner
              </h1>
              <p className="text-lg md:text-xl text-gray-500 leading-relaxed mb-8">
                Our main focus is to help Australians achieve their property dreams. 
                Our experienced team is dedicated to finding you the best home loan solution.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFsJTIwY29ycG9yYXRlJTIwdGVhbSUyMHBvcnRyYWl0JTIwc21pbGluZyUyMG9mZmljZXxlbnwwfHx8fDE3NzAwMDI3OTB8MA&ixlib=rb-4.1.0&q=85"
                  alt="MortgageAU Team"
                  className="w-full h-[450px] object-cover"
                  data-testid="about-hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#6B7280] font-medium text-sm tracking-wide uppercase">Our Story</span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#374151] mt-4 mb-6">
                Built on Trust, Driven by Results
              </h2>
              <div className="space-y-4 text-gray-500 leading-relaxed">
                <p>
                  Loans and Beyond was founded with a simple mission: to help everyday Australians 
                  navigate the complex world of home loans without the stress and confusion.
                </p>
                <p>
                  After seeing too many people struggle with confusing loan products and pushy salespeople, 
                  we decided to build a mortgage broking business that puts clients first.
                </p>
                <p>
                  Our success isn't measured in dollars – it's measured in the families we've helped 
                  move into their dream homes. We're committed to providing personalised service and 
                  expert guidance every step of the way.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden h-48">
                  <img
                    src="https://images.unsplash.com/photo-1681505531034-8d67054e07f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBmaW5hbmNpYWwlMjBtZWV0aW5nJTIwaGFuZHNoYWtlfGVufDB8fHx8MTc3MDAwMjc2N3ww&ixlib=rb-4.1.0&q=85"
                    alt="Professional meeting"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden h-64">
                  <img
                    src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&auto=format&fit=crop&q=60"
                    alt="Property keys"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden h-64">
                  <img
                    src="https://images.unsplash.com/photo-1758523671087-b256bbbca475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMG1vdmluZyUyMGludG8lMjBuZXclMjBob21lJTIwYm94ZXN8ZW58MHx8fHwxNzcwMDAyNzg1fDA&ixlib=rb-4.1.0&q=85"
                    alt="Family moving in"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden h-48">
                  <img
                    src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&auto=format&fit=crop&q=60"
                    alt="Calculating savings"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-[#F9FAFB]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#6B7280] font-medium text-sm tracking-wide uppercase">Our Values</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#374151] mt-4">
              What We Stand For
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200/50 rounded-2xl p-8 hover:shadow-hover transition-shadow"
              >
                <div className="w-14 h-14 bg-[#F3F4F6] rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-[#374151]" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-[#374151] mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-500 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#6B7280] font-medium text-sm tracking-wide uppercase">Meet The Team</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#374151] mt-4">
              Your Mortgage Experts
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Our experienced team is passionate about helping you achieve your property goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow flex flex-col sm:flex-row gap-6"
                data-testid={`team-member-${index}`}
              >
                <div className="w-full sm:w-40 h-48 sm:h-auto flex-shrink-0 rounded-xl overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-contain bg-gray-50"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-xl text-[#374151]">
                    {member.name}
                  </h3>
                  <p className="text-[#6B7280] font-medium text-sm mb-3">{member.role}</p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{member.bio}</p>
                  <div className="space-y-2 text-sm">
                    <a href={`tel:${member.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-[#6B7280] hover:text-[#374151]">
                      <Phone size={16} />
                      {member.phone}
                    </a>
                    <a href={`mailto:${member.email}`} className="flex items-center gap-2 text-[#6B7280] hover:text-[#374151]">
                      <Mail size={16} />
                      {member.email}
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 md:py-32 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#6B7280] font-medium text-sm tracking-wide uppercase">Why Work With Us</span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#374151] mt-4 mb-8">
                The Loans & Beyond Difference
              </h2>
              
              <div className="space-y-6">
                {[
                  'Access to 40+ lenders for better rate comparison',
                  'No broker fees – lenders pay us, not you',
                  'Personalised service from application to settlement',
                  'Expert guidance on government schemes and grants',
                  'Ongoing support throughout your loan term',
                  'Fast pre-approvals to help you buy with confidence'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#6B7280] mt-0.5 flex-shrink-0" />
                    <span className="text-[#374151] font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/contact" className="mt-8 inline-block">
                <Button 
                  className="bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  data-testid="about-cta"
                >
                  Get In Touch
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&auto=format&fit=crop&q=60"
                  alt="Team collaboration"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
